// components/note-detail/note-detail.jsx
import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import AntEditor from "../editor/anteditor/anteditor";
import useImageUpload from "../../hooks/useImageUpload";
import UploadOverlay from "../upload/UploadOverlay";
import ItemAddress from "../item-address/ItemAddress";
import {
  db,
  firebase,
  addNote,
  updateNoteTitle,
  updateNoteText,
  updateNoteHeader,
  updateNotePrivacy,
  updateNoteReadonly,
  addNoteLabel,
  deleteNoteLabel,
} from "../../firebase/firebase.utils";
import Labels from "./labels/labels.component";
import { Tooltip } from "react-tooltip";
import {
  BsImage,
  BsFullscreen,
  BsFullscreenExit,
  BsLockFill,
  BsUnlock,
  BsArrowsCollapse,
  BsArrowsExpand,
} from "react-icons/bs";
import { IoTrashOutline } from "react-icons/io5";
import {
  HiXMark,
  HiOutlinePencilSquare,
  HiOutlineBookOpen,
} from "react-icons/hi2";
import "./note-detail.styles.scss";
// import "../../../theme.styles.scss";
let initialState = {
  url: "",
  title: "",
  text: "",
  isPinned: "",
  isArchived: "",
  labels: [],
  color: "",
  collaborators: [],
  reminder: "",
};
export default function NoteDetail({ mode }) {
  const currentUser = useSelector((state) => state.user.currentUser);
  const { noteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const currentSpaceId = location.pathname.split("/")[2];
  const currentStationId = location.pathname.split("/")[4];
  const quickNotes = useSelector((state) => state.notes.notes);
  const prjNotes = useSelector((state) => state.prjnotes.prjnotes);
  const [initialValue, setInitialValue] = useState(null);
  const [note, setNote] = useState(initialState);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const fileRef = useRef();
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [isUploadingHeader, setIsUploadingHeader] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [isArchived, setIsArchived] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [color, setColor] = useState("");
  const [collaborators, setCollaborators] = useState([]);
  const [reminder, setReminder] = useState("");
  const [labels, setLabels] = useState([]);
  const [label, setLabel] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const savingCounterRef = useRef(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [noteContent, setNoteContent] = useState(initialValue);
  const noteEditorRef = useRef(null);
  const [docImgFile, setDocImgFile] = useState(null);
  const [docImgFileAltText, setDocImgFileAltText] = useState("");
  const [minimizedHeader, setMinimizedHeader] = useState(false);
  const [spaceId, setSpaceId] = useState(null);
  const [stationId, setStationId] = useState(null);
  const { uploading, progress, status, uploadImage } = useImageUpload({
    userId: currentUser?.uid,
    getBasePath: () => {
      if (!currentUser?.uid || !spaceId) return "";
      if (spaceId === `${currentUser.uid}-QuickNotes`) return `users/${currentUser.uid}/notesImages`;
      return stationId ? `space/${spaceId}/stations/${stationId}/notesImages` : "";
    },
  });
  const headerBusy = useRef(false);
  const titleRef = useRef();
  // const contentRef = useRef();
  const pendingRemoteHTMLRef = useRef(null);
  const storedNote = mode === "edit"
    ? prjNotes.find((note) => note.id === noteId) || quickNotes.find((note) => note.id === noteId)
    : null;
  const [liveNote, setLiveNote] = useState(null);
  const sourceSpace = storedNote?.fromSpaceId || currentSpaceId;
  const sourceStation = storedNote?.fromStationId || currentStationId;
  const notePath = mode === "edit" && currentUser?.uid && noteId && sourceSpace
    ? sourceSpace === `${currentUser.uid}-QuickNotes`
      ? `users/${currentUser.uid}/notes/${noteId}`
      : sourceStation ? `space/${sourceSpace}/stations/${sourceStation}/notes/${noteId}` : null
    : null;
  useEffect(() => {
    if (!notePath) return;
    return db.doc(notePath).onSnapshot(snapshot=>setLiveNote({path:notePath,data:snapshot.exists ? {...snapshot.data(),id:snapshot.id} : null}),error=>console.log("Note moderation update:",error));
  }, [notePath]);
  const refNote = liveNote?.path === notePath ? liveNote.data : storedNote;
  const contentRemoved = refNote?.moderationHidden === true;
  useEffect(() => {
    if (mode !== "edit") return;
    if (refNote) {
      setNote(refNote);
      // setCustomEditorState(refNote.text);
      setInitialValue(refNote.text);
      setNoteContent(refNote.text);
      // generateHtml("add");
      setTitle(refNote.title);
      setText(refNote.text);
      setUrl(refNote.url || "");
      setIsPinned(refNote.isPinned);
      setIsArchived(refNote.isArchived);
      setColor(refNote.color);
      setCollaborators(refNote.collaborators);
      setReminder(refNote.reminder);
      setLabels(refNote.labels);
      setIsPrivate(refNote.private);
      setSpaceId(refNote.fromSpaceId);
      setStationId(refNote.fromStationId);
      setIsReadOnly(!!refNote.readOnly);
    } else {
      // setNote(initialState);
      setNoteContent(initialValue);
    }
  }, [refNote, noteId, mode]);
  useEffect(() => {
    if (mode !== "add" || !currentUser?.uid) return;
    setSpaceId(currentSpaceId || `${currentUser.uid}-QuickNotes`);
    setStationId(currentStationId || null);
  }, [mode, currentSpaceId, currentStationId, currentUser?.uid]);
  async function uploadHeaderImage(file) {
    const { url } = await uploadImage(file);
    return url;
  }
  function handleImageClick() {
    fileRef.current.click();
  }
  async function handleImage(event) {
    const pickedFile = event.target.files[0];
    event.target.value = "";
    if (!pickedFile || uploading || headerBusy.current) return;
    headerBusy.current = true;
    setIsUploadingHeader(true);
    try {
      const headerUrl = await uploadHeaderImage(pickedFile);
      // if in edit mode, immediately write to Firestore
      if (mode === "edit" && noteId) {
        await updateNoteHeader(
          currentSpaceId,
          currentStationId,
          noteId,
          headerUrl,
          currentUser.userName,
        );
      }
      setUrl(headerUrl);
      setFilePreview(null);
      setFile(null);
    } catch (err) {
      console.error("Header‐image upload failed:", err);
      alert("Could not upload header image. Please try again.");
    } finally {
      headerBusy.current = false;
      setIsUploadingHeader(false);
    }
  }
  async function removeImageFile() {
    if (uploading || headerBusy.current) return;
    headerBusy.current = true;
    const oldUrl = url;
    try {
      if (mode === "edit" && noteId) {
        await updateNoteHeader(currentSpaceId, currentStationId, noteId, "", currentUser.userName);
      }
      setUrl("");
      setNote((previous) => ({ ...previous, url: "" }));
      setFilePreview(null);
      setFile(null);
      if (oldUrl) {
        await firebase.storage().refFromURL(oldUrl).delete().catch((error) => {
          if (error.code !== "storage/object-not-found") console.error("Header file cleanup failed:", error);
        });
      }
    } catch (error) {
      alert("Could not remove the header. Please try again.");
    } finally {
      headerBusy.current = false;
    }
  }
  // AUTOSAVE of TITLE;
  useEffect(() => {
    if (contentRemoved) return;
    if (mode === "edit") {
      if (titleRef.current !== title) {
        const timer = setTimeout(() => {
          updateNoteTitle(currentSpaceId, currentStationId, noteId, title);
          console.log("trigged title save");
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [title, contentRemoved]);
  // AUTOSAVE of NOTE CONTENT;
  useEffect(() => {
    if (contentRemoved) return;
    if (mode === "edit") {
      if (initialValue !== noteContent) {
        console.log("saving...");
        const timer = setTimeout(() => {
          (async () => {
            try {
              beginSaving();
              await updateNoteText(
                currentSpaceId,
                currentStationId,
                noteId,
                noteContent,
                currentUser.userName,
              );
              console.log("note is autosaved");
            } catch (e) {
              console.error("Autosave failed:", e);
            } finally {
              endSaving();
            }
          })();
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [noteContent, contentRemoved]);
  // AUTOSAVE of TEXT;
  // useEffect(() => {
  //   if (mode === "edit") {
  //     const contenttimer = setTimeout(() => {
  //       if (text !== htmlfromserializer) {
  //         updateNoteText(
  //           currentSpaceId,
  //           currentStationId,
  //           noteId,
  //           htmlfromserializer,
  //           currentUser.userName
  //         );
  //         console.log("trigged content save");
  //       }
  //     }, 3000);
  //     return () => clearTimeout(contenttimer);
  //   }
  // }, [htmlfromserializer]);
  async function handleSaveNote() {
    if (mode === "add") {
      if (!title.trim()) {
        alert("Please enter a title");
        return;
      }
      const isQuickNote = spaceId === `${currentUser.uid}-QuickNotes`;
      if (!spaceId || (!isQuickNote && !stationId)) {
        alert("Please select a project or Quick Notes");
        return;
      }
      if (isSaving || isUploadingHeader || uploading) return;
      try {
        beginSaving();
        let headerUrl = url;
        if (file && !headerUrl) {
          headerUrl = await uploadHeaderImage(file);
          setUrl(headerUrl);
        }
        await addNote(
          spaceId,
          stationId,
          title,
          noteContent || "",
          headerUrl,
          isPinned,
          isArchived,
          color,
          collaborators,
          reminder,
          labels,
          currentUser.userName,
          isPrivate,
          currentUser.uid,
        );
        navigate(-1);
      } catch (error) {
        console.error("Error creating note:", error);
        alert("Could not save the note. Please try again.");
      } finally {
        endSaving();
      }
    } else if (mode === "edit") {
      updateNoteText(
        currentSpaceId,
        currentStationId,
        noteId,
        noteContent,
        currentUser.userName,
      );
      navigate(-1);
    }
  }
  function handleExitForm() {
    if (mode === "edit") {
      if (initialValue !== noteContent) {
        updateNoteText(
          currentSpaceId,
          currentStationId,
          noteId,
          noteContent,
          currentUser.userName,
        );
      }
    }
    setLabels([]);
    setLabel("");
    navigate(-1);
  }
  function handleExitFormOnBackdropPress() {
    if (mode === "edit") {
      if (initialValue !== noteContent) {
        updateNoteText(
          currentSpaceId,
          currentStationId,
          noteId,
          noteContent,
          currentUser.userName,
        );
      }
      setLabels([]);
      setLabel("");
      navigate(-1);
    }
  }
  function handleUpdatePrivacy(newprivacy) {
    setIsPrivate(newprivacy);
    if (mode === "edit") {
      updateNotePrivacy(
        currentSpaceId,
        currentStationId,
        noteId,
        newprivacy,
        currentUser.userName,
      );
    }
    // setLabels([]);
    // setLabel("");
    // navigate(-1);
  }
  function handleUpdateReadOnly(newreadonly) {
    setIsReadOnly(newreadonly);
    if (mode === "edit") {
      updateNoteReadonly(
        currentSpaceId,
        currentStationId,
        noteId,
        newreadonly,
        currentUser.userName,
      );
    }
  }
  const handleChange = (e) => {
    setNote({ ...note, text: "anything" });
  };
  const handleLabelSubmit = (e) => {
    e.preventDefault();
    if (label !== "") {
      if (mode === "edit") {
        addNoteLabel(currentSpaceId, currentStationId, noteId, label);
      } else {
        var arr = note.labels;
        arr.push(label);
        setLabels(arr);
        // setNote({ ...note, labels: arr });
      }
    }
    setLabel("");
  };
  const handleRemoveLabel = (lbl) => {
    if (mode === "edit") {
      deleteNoteLabel(currentSpaceId, currentStationId, noteId, lbl);
    } else {
      let array = note.labels;
      const index = array.indexOf(lbl);
      if (index > -1) {
        // only splice array when item is found
        const arrayref = array.splice(index, 1); // 2nd parameter means remove one item only
        setLabels(arrayref);
        // updateNote({ ...note, labels: arrayref });
      }
      // setNote({ ...note, labels: array });
    }
  };
  const handleUploadDocImage = async () => {
    const { url } = await uploadImage(docImgFile);
    return url;
  };
  function handleTitleChange(e) {
    note.title = e.target.value;
  }
  function beginSaving() {
    savingCounterRef.current += 1;
    setIsSaving(true);
  }
  function endSaving() {
    savingCounterRef.current = Math.max(0, savingCounterRef.current - 1);
    if (savingCounterRef.current === 0) setIsSaving(false);
  }
  if (contentRemoved) return <section className="note-detail" style={{padding:24}}><p role="status">This note was removed by moderation.</p><button type="button" onClick={()=>navigate(-1)}>Back</button></section>;

  return (
    <div className="notes__form-container">
      <UploadOverlay visible={uploading || isUploadingHeader} progress={uploading ? progress : 100}
        status={uploading ? status : "Saving header"} />
      <div
        className={
          fullscreen ? "notes__form-cover-fullscreen" : "notes__form-cover"
        }
        onMouseDown={handleExitFormOnBackdropPress}
      >
        <div
          className={
            fullscreen
              ? "notes__form-content-fullscreen"
              : "notes__form-content"
          }
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div className="notes__form-scroll">
            {url ? (
              <div className="notes__form-image-preview">
                <img
                  src={url}
                  alt="preview"
                  style={{
                    objectFit: "cover",
                    height: minimizedHeader ? 60 : "100%",
                  }}
                />
                <div
                  className="notes__form-image-delete-btn"
                  onClick={removeImageFile}
                >
                  <IoTrashOutline size="1.5em" />
                </div>
                <div
                  onClick={() => setMinimizedHeader(!minimizedHeader)}
                  className="notes__form-image-minimize-btn"
                >
                  {minimizedHeader ? (
                    <BsArrowsExpand size="1.5em" />
                  ) : (
                    <BsArrowsCollapse size="1.5em" />
                  )}
                </div>
              </div>
            ) : filePreview ? (
              <div className="notes__form-image-preview">
                <img
                  src={filePreview}
                  alt="preview"
                  style={{
                    objectFit: "cover",
                    height: minimizedHeader ? 50 : "100%",
                  }}
                />
                <div className="notes__form-image-delete-btn">
                  <IoTrashOutline size="1.5em" />
                </div>
                <div
                  onClick={() => setMinimizedHeader(!minimizedHeader)}
                  className="notes__form-image-minimize-btn"
                >
                  {minimizedHeader ? (
                    <BsArrowsExpand size="1.5em" />
                  ) : (
                    <BsArrowsCollapse size="1.5em" />
                  )}
                </div>
              </div>
            ) : null}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                paddingTop: fullscreen ? 0 : 5,
                paddingRight: 16,
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  display: fullscreen ? "none" : "block",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <ItemAddress
                  type="note"
                  spaceId={spaceId}
                  stationId={stationId}
                  setSpaceId={setSpaceId}
                  setStationId={setStationId}
                  compactWorkspace={false}
                  style={{ paddingTop: 0, paddingRight: 0 }}
                />
              </div>
              {!fullscreen && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 20,
                    flexShrink: 0,
                    marginLeft: "auto",
                  }}
                >
                  {!filePreview && !note?.url && (
                    <div
                      className="notes__form-button"
                      style={{ marginRight: 0 }}
                    >
                      <button onClick={handleImageClick}>
                        <BsImage
                          size="1.2em"
                          data-tooltip-id="addCoverImage-how"
                        />
                        <span />
                      </button>
                      <input
                        hidden
                        ref={fileRef}
                        type="file"
                        onChange={handleImage}
                      />
                    </div>
                  )}
                  <div
                    className="notes__form-fullscreen-btn"
                    onClick={() => {
                      setFullscreen(!fullscreen);
                      if (!fullscreen) {
                        setMinimizedHeader(true);
                      }
                    }}
                  >
                    {fullscreen ? (
                      <BsFullscreenExit
                        size="1.2em"
                        data-tooltip-id="turnOffFullScreen-how"
                      />
                    ) : (
                      <BsFullscreen
                        size="1em"
                        data-tooltip-id="turnOnFullScreen-how"
                      />
                    )}
                  </div>
                  <div
                    className="notes__form-exit-btn"
                    onClick={handleExitForm}
                  >
                    <HiXMark size="2.2em" />
                  </div>
                </div>
              )}
            </div>
            <div className="notes__form-input-header">
              <input
                className="notes__form-input-title"
                placeholder="Note title"
                type="text"
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              {fullscreen && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 20,
                    flexShrink: 0,
                    marginLeft: "auto",
                  }}
                >
                  {!filePreview && !note?.url && (
                    <div
                      className="notes__form-button"
                      style={{ marginRight: 0 }}
                    >
                      <button onClick={handleImageClick}>
                        <BsImage
                          size="1.2em"
                          data-tooltip-id="addCoverImage-how"
                        />
                        <span />
                      </button>
                      <input
                        hidden
                        ref={fileRef}
                        type="file"
                        onChange={handleImage}
                      />
                    </div>
                  )}
                  <div
                    className="notes__form-fullscreen-btn"
                    onClick={() => {
                      setFullscreen(!fullscreen);
                      if (!fullscreen) {
                        setMinimizedHeader(true);
                      }
                    }}
                  >
                    {fullscreen ? (
                      <BsFullscreenExit
                        size="1.2em"
                        data-tooltip-id="turnOffFullScreen-how"
                      />
                    ) : (
                      <BsFullscreen
                        size="1em"
                        data-tooltip-id="turnOnFullScreen-how"
                      />
                    )}
                  </div>
                  <div
                    className="notes__form-exit-btn"
                    onClick={handleExitForm}
                  >
                    <HiXMark size="2.2em" />
                  </div>
                </div>
              )}
            </div>
            <AntEditor
              ref={pendingRemoteHTMLRef}
              id="editorNote"
              key={noteId}
              mainToolbarOn={true}
              floatingToolbarOn={true}
              placeholder={
                "Here you can take project notes, create rich-text docs, can even create your own wiki system..."
              }
              initialValue={initialValue}
              setDocContent={setNoteContent}
              setDocImgFile={setDocImgFile}
              setDocImgFileAltText={setDocImgFileAltText}
              docImgFileAltText={docImgFileAltText}
              handleUploadDocImage={handleUploadDocImage}
              editability={isReadOnly ? false : true}
            />
          </div>
          <div className="notes__form-actions">
            <Labels
              labels={labels}
              handleLabelSubmit={handleLabelSubmit}
              handleRemoveLabel={handleRemoveLabel}
              setLabel={setLabel}
              label={label}
              refKey="from-note-detail"
            />
            <div className="notes__form-actions-sub">
              {isPrivate ? (
                <button
                  className="notes__form-privacy-btn"
                  onClick={() => handleUpdatePrivacy(false)}
                  data-tooltip-id="turnOffPrivate-how"
                >
                  <BsLockFill />
                </button>
              ) : (
                <button
                  className="notes__form-privacy-btn"
                  onClick={() => handleUpdatePrivacy(true)}
                  data-tooltip-id="turnOnPrivate-how"
                >
                  <BsUnlock />
                </button>
              )}
              {isReadOnly ? (
                <button
                  className="notes__form-privacy-btn"
                  onClick={() => handleUpdateReadOnly(false)}
                  data-tooltip-id="turnOnReadOnly-how"
                >
                  <HiOutlinePencilSquare />
                </button>
              ) : (
                <button
                  className="notes__form-privacy-btn"
                  onClick={() => handleUpdateReadOnly(true)}
                  data-tooltip-id="turnOffReadOnly-how"
                >
                  <HiOutlineBookOpen />
                </button>
              )}
              {noteId && (
                <div
                  className={`notes__form-button${
                    isSaving ? " is-disabled" : ""
                  }`}
                  onClick={isSaving ? undefined : handleSaveNote}
                  aria-disabled={isSaving}
                  style={
                    isSaving
                      ? { opacity: 0.6, pointerEvents: "none" }
                      : undefined
                  }
                >
                  {isSaving ? "Saving…" : "Save"}
                </div>
              )}
              {mode === "add" && (
                <>
                  {/* <button
                    className="notes__form-button"
                    onClick={handleSaveNote}
                    disabled={title === ""}
                  >
                    Save & Close
                  </button> */}
                  <button
                    className="notes__form-button"
                    onClick={handleSaveNote}
                    disabled={
                      title.trim() === "" || isUploadingHeader || uploading || isSaving
                    }
                  >
                    {isUploadingHeader ? "Uploading header…" : "Save & Close"}
                  </button>
                </>
              )}
            </div>
          </div>
          <Tooltip id="turnOnPrivate-how">
            <div className="cal__gCal-tip" style={{ zIndex: 8 }}>
              Make the note visible only to you.
            </div>
          </Tooltip>
          <Tooltip id="turnOffPrivate-how" style={{ zIndex: 8 }}>
            <div className="cal__gCal-tip">
              Make the note visible to all workspace and project members.
            </div>
          </Tooltip>
          <Tooltip id="turnOnReadOnly-how">
            <div className="cal__gCal-tip" style={{ zIndex: 7 }}>
              Make the note read-only for others.
            </div>
          </Tooltip>
          <Tooltip id="turnOffReadOnly-how" style={{ zIndex: 7 }}>
            <div className="cal__gCal-tip">
              Make the note editable by others.
            </div>
          </Tooltip>
          <Tooltip id="addCoverImage-how" style={{ zIndex: 7 }}>
            <div className="cal__gCal-tip">Add cover image.</div>
          </Tooltip>
          <Tooltip id="turnOnFullScreen-how" style={{ zIndex: 7 }}>
            <div className="cal__gCal-tip">Fullscreen</div>
          </Tooltip>
          <Tooltip id="turnOnFullScreen-how" style={{ zIndex: 7 }}>
            <div className="cal__gCal-tip">Fullscreen</div>
          </Tooltip>
          <Tooltip id="turnOffFullScreen-how" style={{ zIndex: 7 }}>
            <div className="cal__gCal-tip">Turn-off fullscreen</div>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
